import React from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { Input, FormGroup, Checkbox, Button, Card } from "@cofano/cck";
import { Box, Grid2 as Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import RelationContactTypeSelect from "components/selects/RelationContactTypeSelect";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language, usePermission } from "imports/Utils";

import CharterContactRoleTypeSelect from "view/charters/components/selects/CharterContactRoleTypeSelect";
import { RelationSchemaInput, RelationSchemaOutput } from "view/relations/utils/relation.schema";
import {
	showCommunicateStatusChanges,
	showContactRoleType,
	showDefaultContact,
	showVisibleForShip,
} from "view/relations/utils/RelationUtils";

const RelationContactsForm = () => {
	const session = useStoreState(state => state.session);

	const { isSkipperOrShipManager } = usePermission();

	const { control, watch } = useFormContext<RelationSchemaInput, unknown, RelationSchemaOutput>();

	const values = watch();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "contacts",
	});

	const [ contacts, isSkipper, isShipManager ] = useWatch({ control, name: [ "contacts", "isSkipper", "isShipManager" ] });

	const defaultContact = {
		id: null,
		type: null,
		contactRoleTypes: null,
		name: "",
		insertion: "",
		surName: "",
		email: "",
		telephone: "",
		defaultContact: false,
		visibleForShip: isSkipper || isShipManager || false,
		communicateStatusChanges: false,
	};

	return (
		<Box p={2} pt={fields.length === 0 ? 2 : 0}>
			{fields.map((item, index) => {
				return (
					<Card
						key={item.id}
						title={Language("CONTACT")}
						subtitle={contacts?.[index]?.name}
						actions={[ {
							onClick: () => remove(index),
							icon: <DeleteIcon />,
						} ]}
						collapsable>
						<Grid spacing={3} container>
							<Grid size={12}>
								<FormGroup label={Language("TYPE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.type`}
										control={control}
										render={({ field }) => <RelationContactTypeSelect {...field} />} />
								</FormGroup>
							</Grid>

							{showContactRoleType(values) && (
								<Grid size={12}>
									<FormGroup label={Language("CONTACT_ROLE_TYPE")} overflow="hidden">
										<Controller
											name={`contacts.${index}.contactRoleTypes`}
											control={control}
											render={({ field }) => <CharterContactRoleTypeSelect multiple {...field} />} />
									</FormGroup>
								</Grid>
							)}

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								<FormGroup label={Language("SHIPS_NAME")} overflow="hidden" required>
									<Controller
										name={`contacts.${index}.name`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								<FormGroup label={Language("INSERTION")} overflow="hidden">
									<Controller
										name={`contacts.${index}.insertion`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								<FormGroup label={Language("SURNAME")} overflow="hidden">
									<Controller
										name={`contacts.${index}.surName`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								<FormGroup label={Language("EMAIL")} overflow="hidden" required>
									<Controller
										name={`contacts.${index}.email`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								<FormGroup label={Language("TELEPHONE")} overflow="hidden">
									<Controller
										name={`contacts.${index}.telephone`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }} />

							{showDefaultContact(values) && (
								<Grid size={{ xs: 12, sm: isSkipperOrShipManager ? 3 : 6, md: isSkipperOrShipManager ? 2 : 4 }}>
									<Controller
										name={`contacts.${index}.defaultContact`}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												checked={field.value}
												label={intl.formatMessage({ id: "DEFAULT_CONTACT" })}
											/>
										)}
									/>
								</Grid>
							)}

							{showVisibleForShip(values) && (
								<Grid size={{ xs: 12, sm: 6, md: 4 }}>
									<Controller
										name={`contacts.${index}.visibleForShip`}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												checked={field.value}
												label={intl.formatMessage({ id: "VISIBLE_FOR_SHIP" })}
											/>
										)}
									/>
								</Grid>
							)}

							{showCommunicateStatusChanges(values, session) && (
								<Grid size={{ xs: 12, sm: 6, md: 4 }}>
									<Controller
										name={`contacts.${index}.communicateStatusChanges`}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												checked={field.value}
												label={intl.formatMessage({ id: "COMMUNICATE_STATUS_CHANGES" })}
											/>
										)}
									/>
								</Grid>
							)}
						</Grid>
					</Card>
				);
			})}

			<Box>
				<Button
					icon={<AddIcon />}
					onClick={() => append(defaultContact)}
					size="small"
					variant="outlined" />
			</Box>
		</Box>
	);
};

export default RelationContactsForm;
